import React, { createContext, useCallback, useContext, useEffect, useMemo, useSyncExternalStore } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import {
  BusinessDateInfo, getBusinessDateInfo, setBusinessDateInfo, setCalendarClosures, subscribeBusinessDate,
} from '../utils/businessDate';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const REFRESH_MS = 60_000;

interface BusinessDateContextValue {
  info: BusinessDateInfo | null;
  /** The book's current business date, YYYY-MM-DD, or null before the first answer. */
  businessDate: string | null;
  /** The date a new ticket defaults to. */
  tradeDate: string | null;
  refresh: () => Promise<void>;
}

const BusinessDateContext = createContext<BusinessDateContextValue>({
  info: null, businessDate: null, tradeDate: null, refresh: async () => undefined,
});

export const useBusinessDate = () => useContext(BusinessDateContext);

/**
 * Keeps the book's business date and the market calendars in step with the server. Every date
 * default on every screen reads from here (through utils/dateUtils), so the browser clock never
 * dates a ticket.
 */
export const BusinessDateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth() as { isAuthenticated: boolean };
  const info = useSyncExternalStore(subscribeBusinessDate, getBusinessDateInfo);

  const refresh = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/tenant-config/business-date`);
      if (response.data?.businessDate) setBusinessDateInfo(response.data as BusinessDateInfo);
    } catch {
      // Keep the last known date. A screen that needs the date to act will be told by the server.
    }
  }, []);

  const loadCalendars = useCallback(async () => {
    try {
      const year = new Date().getFullYear();
      const response = await axios.get(`${API_URL}/calendars/closures`, {
        params: { from: `${year - 3}-01-01`, to: `${year + 6}-12-31` },
      });
      setCalendarClosures(response.data);
    } catch {
      // Weekends still apply; the server adjusts and validates every date it stores.
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setBusinessDateInfo(null);
      return undefined;
    }
    refresh();
    loadCalendars();
    const timer = window.setInterval(refresh, REFRESH_MS);
    const onFocus = () => { refresh(); };
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [isAuthenticated, refresh, loadCalendars]);

  const value = useMemo(() => ({
    info, businessDate: info?.businessDate || null, tradeDate: info?.tradeDate || null, refresh,
  }), [info, refresh]);

  return <BusinessDateContext.Provider value={value}>{children}</BusinessDateContext.Provider>;
};
